/**
 * Bruit aéronautique — Plan d'Exposition au Bruit (PEB) et Plan de Gêne
 * Sonore (PGS) autour des aérodromes français.
 *
 * Pourquoi c'est un red flag immo de premier plan :
 *   - Zone PEB A : zone de bruit fort, construction de logements quasi
 *     totalement interdite. Un bien existant peut être habité mais
 *     toute extension/changement de destination est bloqué.
 *   - Zone PEB B : interdiction de construire ou d'agrandir les
 *     logements. Beaucoup de banques refusent le crédit en zone B.
 *   - Zone PEB C : logements neufs autorisés sous conditions strictes
 *     (densité plafonnée, isolation renforcée). Restrictions urba.
 *   - Zone PEB D : pas de restriction de construction, mais le notaire
 *     doit le mentionner dans l'acte de vente. Effet psychologique
 *     non négligeable sur le prix.
 *   - Zone PGS I/II/III : éligibilité à l'aide à l'insonorisation
 *     (subvention plafonnée 80 % des travaux). Pas bloquant, plutôt
 *     un + pour l'acquéreur.
 *
 * Sources : Géoplateforme WMS (DGAC).
 *   - `dgac_peb_plan_wmsv` : zonage PEB (A/B/C/D)
 *   - `dgac_pgs_plan_wmsv` : zonage PGS (I/II/III)
 *
 * Stratégie de requête : on fait deux WMS GetFeatureInfo en parallèle,
 * fenêtre ~100 m centrée sur le point geocode, INFO_FORMAT JSON.
 * On NE peut PAS interroger en WFS — la couche `*_arrete_wfs` ne
 * contient que les points aérodromes, pas les polygones de zonage.
 *
 * Cache : 90 jours. Un PEB est révisé tous les 5-10 ans (procédure
 * lourde), un PGS plus rarement encore — un cache long est largement
 * justifié par le coût d'invalidation upstream.
 *
 * Limitation connue : on ne couvre PAS le bruit terrestre (CSITT
 * routier/ferroviaire, CBS Lden des grandes agglo). Ces couches ne
 * sont pas exposées centralement par la Géoplateforme — chaque
 * préfecture publie son arrêté sur son propre portail. Extension
 * possible si la demande utilisateur le justifie.
 */

import { useMemo } from "react";
import { useEnvBlock, coordKey } from "./shared";
import type { FicheDoc } from "../fiches";

const WMSV_ENDPOINT = "https://data.geopf.fr/wms-v/ows";
const BRUIT_TTL_DAYS = 90;

// ─── Types ───────────────────────────────────────────────────────────────

export type BruitSource = "peb" | "pgs";

// Sévérité unifiée par zone — utilisée pour trier et choisir la pire zone
// quand la parcelle est dans plusieurs zonages superposés (rare mais
// possible quand deux aérodromes sont proches, ex. CDG + Le Bourget).
const SEVERITY: Record<string, number> = {
  "peb-A": 0, // pire
  "peb-B": 1,
  "peb-C": 2,
  "peb-D": 3,
  "pgs-1": 4,
  "pgs-2": 5,
  "pgs-3": 6, // moins pire
};

export type BruitFeature = {
  source: BruitSource;
  // Zone normalisée : "A"/"B"/"C"/"D" pour PEB, "1"/"2"/"3" pour PGS.
  // L'API DGAC renvoie déjà des chiffres arabes pour le PGS.
  zone: string;
  // Niveau Lden (dB) au bord intérieur de la zone (vers la piste).
  ldenInt: number | null;
  // Niveau Lden (dB) au bord extérieur de la zone (côté résidentiel).
  ldenExt: number | null;
  // Code OACI de l'aérodrome (LFPG, LFPO, LFMN, …).
  oaci: string;
  // Nom long de l'aérodrome.
  airport: string;
  // Date de l'arrêté préfectoral d'approbation (ISO).
  arreteDate: string | null;
  // PDF officiel de l'arrêté (toujours dispo sur les enregistrements DGAC).
  arreteUrl: string | null;
};

export type BruitData = {
  // Nombre total de zones détectées sous le point.
  count: number;
  // Toutes les features triées par sévérité décroissante.
  features: BruitFeature[];
  // Pire zone (= la première de `features` quand count > 0).
  worst: { source: BruitSource; zone: string; airport: string } | null;
};

// ─── Fetcher ─────────────────────────────────────────────────────────────

type WmsFeature = {
  type: "Feature";
  properties: Record<string, unknown>;
};
type WmsResponse = {
  type: "FeatureCollection";
  features: WmsFeature[];
};

async function queryWmsLayer(
  source: BruitSource,
  lat: number,
  lng: number,
): Promise<BruitFeature[]> {
  const layerName =
    source === "peb" ? "dgac_peb_plan_wmsv" : "dgac_pgs_plan_wmsv";
  // Fenêtre ~100 m centrée. Pas besoin de plus serré : le WMS interroge
  // le pixel exact (I=50, J=50 sur 101×101), donc on est de facto en
  // requête ponctuelle. Une fenêtre trop fine (< 0.0001°) fait parfois
  // échouer le serveur sur l'arrondi BBOX/CRS.
  const halfDeg = 0.0005;
  const bbox = `${(lat - halfDeg).toFixed(6)},${(lng - halfDeg).toFixed(6)},${(lat + halfDeg).toFixed(6)},${(lng + halfDeg).toFixed(6)}`;
  const url = new URL(WMSV_ENDPOINT);
  url.searchParams.set("SERVICE", "WMS");
  url.searchParams.set("VERSION", "1.3.0");
  url.searchParams.set("REQUEST", "GetFeatureInfo");
  url.searchParams.set("LAYERS", layerName);
  url.searchParams.set("QUERY_LAYERS", layerName);
  url.searchParams.set("STYLES", "");
  // CRS:84 ou EPSG:4326 — en WMS 1.3.0, EPSG:4326 = (lat,lng) (axes
  // inversés vs. 1.1.1). On reste en EPSG:4326 et on construit la BBOX
  // en (latMin,lngMin,latMax,lngMax) cohérent.
  url.searchParams.set("CRS", "EPSG:4326");
  url.searchParams.set("BBOX", bbox);
  url.searchParams.set("WIDTH", "101");
  url.searchParams.set("HEIGHT", "101");
  url.searchParams.set("I", "50");
  url.searchParams.set("J", "50");
  url.searchParams.set("FORMAT", "image/png");
  url.searchParams.set("INFO_FORMAT", "application/json");

  const res = await fetch(url.toString(), {
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(15_000),
  });
  if (!res.ok) {
    throw new Error(`WMS ${layerName} HTTP ${res.status}`);
  }
  const body = (await res.json()) as WmsResponse;
  const features = Array.isArray(body.features) ? body.features : [];
  return features
    .map((f) => parseFeature(source, f.properties ?? {}))
    .filter((x): x is BruitFeature => x !== null);
}

function parseFeature(
  source: BruitSource,
  p: Record<string, unknown>,
): BruitFeature | null {
  const rawZone = p.zone;
  if (typeof rawZone !== "string" && typeof rawZone !== "number") return null;
  const zone = String(rawZone).trim().toUpperCase();
  // Garde-fou : si la zone n'est pas un code attendu, on rejette plutôt
  // que d'afficher du bruit ("ZUL" ou autre artefact possible).
  if (source === "peb" && !["A", "B", "C", "D"].includes(zone)) return null;
  if (source === "pgs" && !["1", "2", "3"].includes(zone)) return null;

  // Le PEB et le PGS utilisent des noms de champs *différents* pour le Lden,
  // c'est un quirk de l'API DGAC :
  //   - PEB : `indldenext`, `indldenint` (entiers ou strings selon les arrêtés)
  //   - PGS : `indice_lde` (≈ ext), `indice_l_1` (≈ int)
  const ldenExt =
    source === "peb"
      ? toNumber(p.indldenext)
      : toNumber(p.indice_lde);
  const ldenInt =
    source === "peb"
      ? toNumber(p.indldenint)
      : toNumber(p.indice_l_1);

  const oaci = typeof p.code_oaci === "string" ? p.code_oaci : "";
  const airport = typeof p.nom === "string" ? p.nom : "";
  // Le PEB capitalise `date_arret`, le PGS `date_arrete` — encore un quirk.
  const arreteDateRaw =
    source === "peb"
      ? (p.date_arret ?? p.date_arrete)
      : (p.date_arrete ?? p.date_arret);
  const arreteDate =
    typeof arreteDateRaw === "string" ? arreteDateRaw.replace(/Z$/, "") : null;
  const arreteUrl = typeof p.ref_doc === "string" ? p.ref_doc : null;

  return {
    source,
    zone,
    ldenInt,
    ldenExt,
    oaci,
    airport,
    arreteDate,
    arreteUrl,
  };
}

function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim().length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export async function fetchBruit(
  lat: number,
  lng: number,
): Promise<BruitData> {
  // Les deux requêtes sont indépendantes — `Promise.allSettled` pour qu'un
  // 503 momentané sur un layer ne tue pas l'autre. Si les deux échouent on
  // remonte une erreur, sinon on agrège ce qu'on a.
  const [pebResult, pgsResult] = await Promise.allSettled([
    queryWmsLayer("peb", lat, lng),
    queryWmsLayer("pgs", lat, lng),
  ]);

  const features: BruitFeature[] = [];
  if (pebResult.status === "fulfilled") features.push(...pebResult.value);
  if (pgsResult.status === "fulfilled") features.push(...pgsResult.value);

  if (
    pebResult.status === "rejected" &&
    pgsResult.status === "rejected"
  ) {
    // Les deux layers DGAC ont raté → vraie indispo, on remonte.
    throw new Error(
      `WMS DGAC indisponible (PEB : ${String(pebResult.reason)}, PGS : ${String(pgsResult.reason)})`,
    );
  }

  features.sort((a, b) => severityKey(a) - severityKey(b));
  const worst = features[0]
    ? {
        source: features[0].source,
        zone: features[0].zone,
        airport: features[0].airport,
      }
    : null;

  return { count: features.length, features, worst };
}

function severityKey(f: BruitFeature): number {
  const k = `${f.source}-${f.zone}`;
  return SEVERITY[k] ?? 99;
}

// ─── Hook ────────────────────────────────────────────────────────────────

/**
 * `useBruit` — zonage de bruit aéronautique sous la parcelle.
 *
 * On utilise le centroïde geocode (suffisamment précis : les zones PEB/PGS
 * font plusieurs km de côté, la précision parcelle n'apporte rien). Pas
 * de polygone : un GetFeatureInfo ponctuel suffit et économise un
 * download d'image.
 */
export function useBruit(fiche: FicheDoc | null) {
  const opts = useMemo(() => {
    if (!fiche?.geocode || !fiche.id) return null;
    const { lat, lng } = fiche.geocode;
    return {
      ficheId: fiche.id,
      blockId: "bruit" as const,
      inputsKey: `v1|${coordKey(lat, lng)}`,
      ttlDays: BRUIT_TTL_DAYS,
      fetcher: () => fetchBruit(lat, lng),
    };
  }, [fiche?.id, fiche?.geocode]);

  return useEnvBlock<BruitData>(opts);
}

// ─── Helpers exportés pour le rendu ─────────────────────────────────────

/**
 * Libellé court d'une zone — utilisé dans la card et dans le summary du
 * red flag pour ne pas dupliquer la table de mapping.
 */
export function zoneLabel(f: BruitFeature): string {
  if (f.source === "peb") return `PEB zone ${f.zone}`;
  // PGS : on convertit le chiffre arabe en romain pour respecter l'usage
  // officiel ("Plan de Gêne Sonore — zone I/II/III").
  const roman = f.zone === "1" ? "I" : f.zone === "2" ? "II" : "III";
  return `PGS zone ${roman}`;
}

/**
 * Sévérité visuelle d'une feature — dicte la couleur du bandeau.
 * - "danger" : rouge profond (PEB A/B = interdiction de construire)
 * - "warn"   : ambre (PEB C = restrictions strictes)
 * - "info"   : bleu/neutre (PEB D, PGS = info ou éligibilité aide)
 */
export function zoneTone(f: BruitFeature): "danger" | "warn" | "info" {
  if (f.source === "peb") {
    if (f.zone === "A" || f.zone === "B") return "danger";
    if (f.zone === "C") return "warn";
    return "info"; // D
  }
  return "info"; // PGS jamais bloquant
}
